import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { LocalStorageKeys, Info, ElementClassName, PostAppSessionProps } from '@workflow/ecp-shared/constant/information.const';
import { NotificationConstant } from '@workflow/ecp-shared/constant/notification.const';
import { FlowType } from '@workflow/ecp-shared/model/types';
import { WorkflowService } from '@workflow/service/workflow/workflow.service';
import { StorageWrapperService } from '../storage-wrapper/storage-wrapper.service';
import { UserInfo } from '@workflow/ecp-shared/model/userinfo';
import { NoAppsLoginType } from '@shared/enum';
import { isPlatformBrowser } from '@angular/common';

/**
 * User service to get the user login details
 */
@Injectable({
  providedIn: 'root'
})
export class UserService {
  /**
   * Creates an instance of UserService.
   */
  constructor(private readonly storageWrapperService: StorageWrapperService, private readonly workflowService: WorkflowService) {}

  public isExistingUser = true;

  private invitationEconsent = false;
  /** below subject and observable propaget user response(leave/ stay in page) to
   * route guard or required places
   */
  public confirmToLeaveSub = new Subject<boolean>();
  public get confrimToLeave$(): Observable<boolean> {
    return this.confirmToLeaveSub.asObservable();
  }

  public authSub = new BehaviorSubject<boolean>(false);
  public userSub = new BehaviorSubject<UserInfo>(null);

  /** indicates submitted loan or un-submitted loan */
  public submittedLoan: boolean;

  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /**
   * Set User Info
   * @ param value
   */
  set user(value: UserInfo) {
    this.storageWrapperService.set(LocalStorageKeys.userStorageName, value);
    this.userSub.next(value);
    this.authSub.next(!!this.token);
  }

  /**
   * Get User Info
   */
  get user(): UserInfo {
    const userTemp = this.storageWrapperService.get<UserInfo>(LocalStorageKeys.userStorageName);
    this.userSub.next(userTemp);
    if (userTemp) {
      this.authSub.next(!!userTemp.token);
    }
    return userTemp;
  }

  /**
   * Set Flow Type
   * @ param value
   */
  set flowType(value: FlowType) {
    this.storageWrapperService.set(LocalStorageKeys.flowType, value);
  }

  /**
   * Get Flow Type
   */
  get flowType(): FlowType {
    return this.storageWrapperService.get<FlowType>(LocalStorageKeys.flowType);
  }

  /**
   * Get User Info
   */
  get UserInfo(): UserInfo {
    return this.user;
  }

  /**
   * Indicate if user is authenticated
   */
  get isAuthenticated(): boolean {
    return !!this.token;
  }

  /**
   * Get login token
   */
  get token(): string {
    const storageUserInfo = this.user;
    let userToken: string;

    if (storageUserInfo) {
      userToken = storageUserInfo.token;
    }

    return userToken;
  }

  /** will return isconsumer or not */
  public get isConsumer(): boolean {
    return this.storageWrapperService.get<boolean>(LocalStorageKeys.userloanselection);
  }

  /** will return isconsumer or not */
  public set isConsumer(value: boolean) {
    this.storageWrapperService.set(LocalStorageKeys.userloanselection, value);
  }

  /**
   * Logout from application
   */
  public logout(): void {
    this.removeFromLocalStorage(LocalStorageKeys.userStorageName);
    this.removeFromLocalStorage(LocalStorageKeys.flowType);
    this.removeFromLocalStorage(LocalStorageKeys.allowedRoute);
    this.removeFromLocalStorage(LocalStorageKeys.BPMConsumer);
    this.removeFromLocalStorage(LocalStorageKeys.BPMAccordion);
    this.removeFromLocalStorage(Info.appNumber);
    this.removeFromLocalStorage(Info.allowedRoute);
    this.removeFromLocalStorage(Info.submitStatus);
    this.closeAllPopups();
    if (this.isBrowser) {
      sessionStorage[NotificationConstant.LocalStorage.LogOut_Event] = Info.logout + Math.random();
    }
    this.workflowService.clearAllInstance();
    this.clearAll();
  }

  private closeAllPopups(): void {
    if (this.isBrowser) {
      const element = document.getElementsByTagName(ElementClassName.modalContainer);
      for (let index = element.length - 1; index >= 0; index--) {
        const idlePopup = element[index].getElementsByClassName(ElementClassName.idleTimerPopup);
        if (idlePopup && idlePopup.length > 0) {
          continue;
        }

        if (element[index].parentNode) {
          element[index].parentNode.removeChild(element[index]);
        }
      }
      this.removeBackDropByClassName(ElementClassName.modalBackdrop);
      this.removeBackDropByElementName(ElementClassName.bsModalBackdrop);
      document.body.classList.remove(Info.modalOpen);
      document.body.style.paddingRight = ElementClassName.zeroPx;
    }
  }

  private removeBackDropByClassName(className: string): void {
    const backdrop = document.getElementsByClassName(className);
    this.removeBackdrop(backdrop);
  }

  private removeBackDropByElementName(elementTagName: string): void {
    const backdrop = document.getElementsByTagName(elementTagName);
    this.removeBackdrop(backdrop);
  }

  private removeBackdrop(backdrop: HTMLCollectionOf<Element>): void {
    if (backdrop && backdrop.length > 0) {
      for (let backdropIndex = backdrop.length - 1; backdropIndex >= 0; backdropIndex--) {
        backdrop[backdropIndex].remove();
      }
    }
  }

  public removeFromLocalStorage(key: string): void {
    this.storageWrapperService.remove(key);
  }

  public clearCache() {
    this.storageWrapperService.clearCache();
  }

  public clearAll() {
    this.storageWrapperService.clearAll();
    this.storageWrapperService.clearCache();
  }

  public set loanType(loanType: string) {
    this.addOrRemoveInStorage(PostAppSessionProps.loanType, loanType);
  }
  public set loanProductName(loanProductName: string) {
    this.addOrRemoveInStorage(PostAppSessionProps.loanProductName, loanProductName);
  }
  public set loanGroupIcon(loanGroupIcon: string) {
    this.addOrRemoveInStorage(PostAppSessionProps.loanGroupIcon, loanGroupIcon);
  }
  public set loanProductDisplayName(loanProductDisplayName: string) {
    this.addOrRemoveInStorage(PostAppSessionProps.loanProductDisplayName, loanProductDisplayName);
  }
  public set applicants(applicants: string) {
    this.addOrRemoveInStorage(PostAppSessionProps.applicants, applicants);
  }
  public set membershipTypeName(membershipTypeName: string) {
    this.addOrRemoveInStorage(PostAppSessionProps.membershipTypeName, membershipTypeName);
  }
  public set isYouthMembership(isYouthMembership: boolean) {
    this.addOrRemoveInStorage(PostAppSessionProps.isYouthMembership, isYouthMembership);
  }
  public set appStatus(appStatus: string) {
    this.addOrRemoveInStorage(PostAppSessionProps.appStatus, appStatus);
  }
  public set loanStatus(loanStatus: string) {
    this.addOrRemoveInStorage(PostAppSessionProps.loanStatus, loanStatus);
  }
  public set loanStatusDescription(loanStatusDescription: string) {
    this.addOrRemoveInStorage(PostAppSessionProps.loanStatusDescription, loanStatusDescription);
  }
  public set statusMessage(statusMessage: string) {
    this.addOrRemoveInStorage(PostAppSessionProps.statusMessage, statusMessage);
  }
  public set accountNames(accountNames: string) {
    this.addOrRemoveInStorage(PostAppSessionProps.accountNames, accountNames);
  }
  public set accountsNamesLabel(accountsNamesLabel: string) {
    // Temporarily adding this to regular session storage to see if it reduces flakes.
    if (this.isBrowser) {
      sessionStorage.setItem(PostAppSessionProps.accountsNamesLabel, accountsNamesLabel);
    }
    this.addOrRemoveInStorage(PostAppSessionProps.accountsNamesLabel, accountsNamesLabel);
  }
  public set sourceLoanType(sourceLoanType: string) {
    this.addOrRemoveInStorage(PostAppSessionProps.sourceLoanType, sourceLoanType);
  }
  public set membershipStatus(membershipStatus: string) {
    this.addOrRemoveInStorage(PostAppSessionProps.membershipStatus, membershipStatus);
  }
  public set requestedLoanAmount(requestedLoanAmount: string) {
    this.addOrRemoveInStorage(PostAppSessionProps.requestedLoanAmount, requestedLoanAmount);
  }
  public set decision_amount(decision_amount: string) {
    this.addOrRemoveInStorage(PostAppSessionProps.decision_amount, decision_amount);
  }
  public set decision_rate(decision_rate: string) {
    this.addOrRemoveInStorage(PostAppSessionProps.decision_rate, decision_rate);
  }
  public set decision_term(decision_term: string) {
    this.addOrRemoveInStorage(PostAppSessionProps.decision_term, decision_term);
  }
  public set decision_estMonthlyPayment(decision_estMonthlyPayment: string) {
    this.addOrRemoveInStorage(PostAppSessionProps.decision_estMonthlyPayment, decision_estMonthlyPayment);
  }
  public set selectedLoanProduct(selectedLoanProduct: string) {
    this.addOrRemoveInStorage(PostAppSessionProps.selectedLoanProduct, selectedLoanProduct);
  }
  public set postAppStatusMessage(message: string) {
    this.addOrRemoveInStorage(PostAppSessionProps.postAppStatusMessage, message);
  }
  public set isPostAppNotificationActive(value: boolean) {
    this.addOrRemoveInStorage(PostAppSessionProps.isPostAppNotificationActive, value);
  }
  public set postAppNotificationMessage(message: string) {
    this.addOrRemoveInStorage(PostAppSessionProps.postAppNotificationMessage, message);
  }
  addOrRemoveInStorage(key: string, value) {
    if (value) this.storageWrapperService.set(key, value);
    else this.storageWrapperService.remove(key);
  }
  /** Set appNumber to storage */
  public set appNumber(appNumber: string) {
    this.storageWrapperService.set(Info.appNumber, appNumber);
  }
  /** Set appNumber to storage */
  public set allowedRoutes(route: string) {
    this.storageWrapperService.set(Info.allowedRoute, route);
  }
  /**
   * Set invitation link flag
   * @ param value
   */
  public set invitationLinkFlag(flag: boolean) {
    this.invitationEconsent = flag;
  }
  /**
   * Get invitation Link Flag
   */
  public get invitationLinkFlag(): boolean {
    return this.invitationEconsent;
  }

  /**
   * Gets the user login type based on user information.
   * Determines if the user is logged in via email or cellphone.
   *
   * @returns Returns 'email address' if the user is not a cell phone user and has a valid email,
   *                   otherwise returns 'cell phone'.
   */
  public get userLoginType(): string {
    return !this.user?.isCellPhoneUser && this.user?.email?.toString().length > 0
      ? NoAppsLoginType.EmailAddress
      : NoAppsLoginType.CellPhone;
  }

    /**
     * Set Last Session Token
     * @ param value
     */
  set lastSessionToken(value: boolean) {
    this.storageWrapperService.set(LocalStorageKeys.lastSessionToken, value);
  }

  /**
   * Get Last Session Token
   */
  get lastSessionToken(): boolean {
    return this.storageWrapperService.get<boolean>(LocalStorageKeys.lastSessionToken);
  }
}
